import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminCatalog, type AdminCatalogItem } from "../../lib/admin-catalog";
import {
  createAdminSupplier,
  createAdminSupplierLink,
  deleteAdminSupplierLink,
  fetchAdminSupplier,
  fetchAdminSuppliers,
  updateAdminSupplier,
  type AdminSupplierDetail,
  type AdminSupplierSummary
} from "../../lib/admin-suppliers";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type SupplierEditorState = {
  id: number | null;
  name: string;
  contact: string;
  catalogUrls: string;
};

type LinkEditorState = {
  inventoryId: string;
  inventorySearch: string;
  name: string;
  sku: string;
  price: string;
  quantity: string;
};

const emptySupplierEditor: SupplierEditorState = {
  id: null,
  name: "",
  contact: "",
  catalogUrls: ""
};

const emptyLinkEditor: LinkEditorState = {
  inventoryId: "",
  inventorySearch: "",
  name: "",
  sku: "",
  price: "0",
  quantity: "20"
};

function toSupplierEditorState(supplier: AdminSupplierSummary | AdminSupplierDetail): SupplierEditorState {
  return {
    id: supplier.id,
    name: supplier.name,
    contact: supplier.contact,
    catalogUrls: supplier.catalogUrls.join("\n")
  };
}

function parseCatalogUrls(value: string) {
  return Array.from(new Set(value.split(/\n|,/).map((item) => item.trim()).filter(Boolean)));
}

export function AdminSuppliersPage() {
  const [suppliers, setSuppliers] = useState<AdminSupplierSummary[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<AdminSupplierDetail | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalSuppliers: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalSuppliers: 0,
    mappedSuppliers: 0,
    catalogCount: 0
  });
  const [catalogProducts, setCatalogProducts] = useState<AdminCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [supplierEditor, setSupplierEditor] = useState<SupplierEditorState>(emptySupplierEditor);
  const [linkEditor, setLinkEditor] = useState<LinkEditorState>(emptyLinkEditor);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const metrics = useMemo(
    () => [
      { label: "Total suppliers", value: summary.totalSuppliers },
      { label: "Mapped suppliers", value: summary.mappedSuppliers },
      { label: "Catalog files", value: summary.catalogCount }
    ],
    [summary]
  );

  const filteredCatalogProducts = useMemo(() => {
    const search = linkEditor.inventorySearch.trim().toLowerCase();
    if (!search) {
      return catalogProducts.slice(0, 8);
    }

    return catalogProducts
      .filter((product) => {
        return (
          product.name.toLowerCase().includes(search) ||
          product.slug.toLowerCase().includes(search) ||
          String(product.id).includes(search) ||
          product.category.toLowerCase().includes(search)
        );
      })
      .slice(0, 8);
  }, [catalogProducts, linkEditor.inventorySearch]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("page", String(page));
        params.set("pageSize", "25");

        const [supplierResult, catalogResult] = await Promise.all([
          fetchAdminSuppliers(params),
          fetchAdminCatalog(new URLSearchParams({ active: "active", page: "1", pageSize: "100" }))
        ]);

        if (!ignore) {
          setSuppliers(supplierResult.suppliers);
          setPagination(supplierResult.pagination);
          setSummary(supplierResult.summary);
          setCatalogProducts(catalogResult.products);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load suppliers.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, [page, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchAdminSuppliers(params);
    setSuppliers(result.suppliers);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  function openCreate() {
    setSelectedSupplier(null);
    setSupplierEditor(emptySupplierEditor);
    setLinkEditor(emptyLinkEditor);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
  }

  async function openSupplier(supplier: AdminSupplierSummary) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchAdminSupplier(supplier.id);
      setSelectedSupplier(detail);
      setSupplierEditor(toSupplierEditorState(detail));
      setLinkEditor(emptyLinkEditor);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load supplier details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function handleSupplierSubmit(event: FormEvent) {
    event.preventDefault();

    if (!supplierEditor.name.trim()) {
      setError("Supplier name is required.");
      return;
    }

    setSavingSupplier(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        name: supplierEditor.name,
        contact: supplierEditor.contact,
        catalogUrls: parseCatalogUrls(supplierEditor.catalogUrls)
      };

      let supplierId = supplierEditor.id;

      if (supplierEditor.id) {
        await updateAdminSupplier(supplierEditor.id, payload);
      } else {
        const created = await createAdminSupplier(payload);
        supplierId = typeof created?.supplierId === "number" ? created.supplierId : null;
      }

      await reloadCurrentView();
      if (supplierId) {
        const refreshed = await fetchAdminSupplier(supplierId);
        setSelectedSupplier(refreshed);
        setSupplierEditor(toSupplierEditorState(refreshed));
      }
      setSuccessMessage(supplierEditor.id ? "Supplier changes saved successfully." : "Supplier created successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save supplier.");
    } finally {
      setSavingSupplier(false);
    }
  }

  async function handleCreateLink(event: FormEvent) {
    event.preventDefault();

    if (!selectedSupplier?.id) {
      setError("Create or open a supplier before adding product mappings.");
      return;
    }

    const inventoryId = Number(linkEditor.inventoryId);
    const price = Number(linkEditor.price);
    const quantity = Number(linkEditor.quantity);

    if (!inventoryId || !linkEditor.name.trim() || !linkEditor.sku.trim() || Number.isNaN(price) || Number.isNaN(quantity) || quantity <= 0) {
      setError("Complete all supplier product fields before saving the mapping.");
      return;
    }

    setSavingLink(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await createAdminSupplierLink(selectedSupplier.id, {
        inventoryId,
        name: linkEditor.name,
        sku: linkEditor.sku,
        price,
        quantity
      });

      const refreshed = await fetchAdminSupplier(selectedSupplier.id);
      setSelectedSupplier(refreshed);
      setSupplierEditor(toSupplierEditorState(refreshed));
      setLinkEditor(emptyLinkEditor);
      await reloadCurrentView();
      setSuccessMessage("Supplier product mapping added.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save supplier mapping.");
    } finally {
      setSavingLink(false);
    }
  }

  async function handleDeleteLink(linkId: number) {
    if (!selectedSupplier?.id) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      await deleteAdminSupplierLink(linkId);
      const refreshed = await fetchAdminSupplier(selectedSupplier.id);
      setSelectedSupplier(refreshed);
      setSupplierEditor(toSupplierEditorState(refreshed));
      await reloadCurrentView();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Unable to delete supplier mapping.");
    }
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Suppliers</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight">Supplier relationships</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">Manage supplier contacts, catalog references, and product-specific sourcing details in one place.</p>
        </div>
        <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
          New supplier
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="admin-card rounded-[28px] p-5">
            <input
              className="field-control px-4 py-3 text-sm"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search suppliers by name or contact"
              value={query}
            />
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Supplier</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Catalogs</th>
                    <th className="px-5 py-4">Mapped products</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={4}>
                        Loading suppliers...
                      </td>
                    </tr>
                  ) : suppliers.length > 0 ? (
                    suppliers.map((supplier) => (
                      <tr
                        className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                        key={supplier.id}
                        onClick={() => void openSupplier(supplier)}
                      >
                        <td className="px-5 py-4">
                          <p className="font-semibold text-nv-ink">{supplier.name}</p>
                          <p className="mt-1 text-xs text-nv-slate">#{supplier.id} · {formatDateTime(supplier.addedOn)}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{supplier.contact || "No contact set"}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{supplier.catalogUrls.length}</td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{supplier.linkedProducts}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={4}>
                        No suppliers matched the current view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalSuppliers} suppliers
              </p>
              <div className="flex items-center gap-2">
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
                  Previous
                </button>
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{selectedSupplier ? "Edit supplier" : "Create supplier"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">{selectedSupplier?.name || "Supplier record"}</h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Select a supplier to edit, or create a new supplier record.</div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">Loading supplier details...</div>
            ) : (
              <div className="mt-6 space-y-6">
                {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}

                <form className="space-y-5" onSubmit={(event) => void handleSupplierSubmit(event)}>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Supplier name</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setSupplierEditor((current) => ({ ...current, name: event.target.value }))} required value={supplierEditor.name} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Contact</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setSupplierEditor((current) => ({ ...current, contact: event.target.value }))} placeholder="Phone, email, rep name, or mixed contact details" value={supplierEditor.contact} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Catalog URLs</label>
                    <textarea className="field-control min-h-[120px] px-4 py-3 text-sm" onChange={(event) => setSupplierEditor((current) => ({ ...current, catalogUrls: event.target.value }))} placeholder="One URL per line" value={supplierEditor.catalogUrls} />
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={savingSupplier} type="submit">
                      {savingSupplier ? "Saving..." : supplierEditor.id ? "Save supplier" : "Create supplier"}
                    </button>
                  </div>
                </form>

                {selectedSupplier ? (
                  <>
                    <div className="min-w-0 border-t border-black/6 pt-6 dark:border-white/8">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Product mappings</p>
                          <h3 className="mt-2 text-xl font-semibold tracking-tight text-nv-ink">Supplier SKUs and order defaults</h3>
                        </div>
                      </div>

                      <form className="mt-5 grid min-w-0 gap-4" onSubmit={(event) => void handleCreateLink(event)}>
                        <div className="min-w-0">
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Catalog product</label>
                          <div className="max-w-full overflow-hidden rounded-[24px] border border-black/8 bg-white/85 shadow-[0_18px_40px_rgba(16,32,51,0.06)] dark:border-white/10 dark:bg-white/6">
                            <div className="border-b border-black/6 bg-black/[0.02] px-4 py-3 dark:border-white/8 dark:bg-transparent">
                              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-nv-slate">Search catalog</label>
                              <div className="field-control flex w-full min-w-0 items-center gap-3 px-3 py-3 text-sm">
                                <svg aria-hidden="true" className="h-4 w-4 shrink-0 text-nv-slate" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                                  <path d="m21 21-4.35-4.35" />
                                  <circle cx="11" cy="11" r="6" />
                                </svg>
                                <input
                                  className="min-w-0 flex-1 bg-transparent text-sm text-[var(--nv-field-text)] caret-[var(--nv-field-text)] outline-none placeholder:text-[var(--nv-field-placeholder)]"
                                  onChange={(event) =>
                                    setLinkEditor((current) => ({
                                      ...current,
                                      inventoryId: "",
                                      inventorySearch: event.target.value
                                    }))
                                  }
                                  placeholder="Search by product name, ID, slug, or category"
                                  value={linkEditor.inventorySearch}
                                />
                              </div>
                            </div>
                            <div className="max-h-64 max-w-full overflow-y-auto overflow-x-hidden p-2">
                              {filteredCatalogProducts.length > 0 ? (
                                <div className="space-y-2">
                                  {filteredCatalogProducts.map((product) => {
                                    const selected = linkEditor.inventoryId === String(product.id);
                                    return (
                                      <button
                                        className={`flex w-full min-w-0 items-start justify-between gap-3 rounded-2xl px-3 py-3 text-left transition ${
                                          selected
                                            ? "field-control border-nv-electric/30 bg-nv-electric/8 text-[var(--nv-field-text)] shadow-[0_12px_24px_rgba(29,78,216,0.08)]"
                                            : "field-control border-transparent bg-transparent text-[var(--nv-field-text)] hover:border-black/8 hover:bg-black/[0.03] dark:hover:border-white/10 dark:hover:bg-white/[0.05]"
                                        }`}
                                        key={product.id}
                                        onClick={() =>
                                          setLinkEditor((current) => ({
                                            ...current,
                                            inventoryId: String(product.id),
                                            inventorySearch: product.name,
                                            name: current.name || product.name
                                          }))
                                        }
                                        type="button"
                                      >
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate font-semibold">{product.name}</p>
                                          <p className="mt-1 text-xs text-nv-slate">
                                            #{product.id} · {product.category}
                                          </p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                          <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${selected ? "text-nv-electric" : "text-nv-slate"}`}>
                                            Stock
                                          </p>
                                          <p className="mt-1 text-sm font-semibold">{product.quantity}</p>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="rounded-2xl border border-dashed border-black/10 px-4 py-5 text-sm text-nv-slate dark:border-white/10">
                                  No catalog products matched that search.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Supplier product name</label>
                          <input className="field-control px-4 py-3 text-sm" onChange={(event) => setLinkEditor((current) => ({ ...current, name: event.target.value }))} value={linkEditor.name} />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-3">
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">SKU</label>
                            <input className="field-control px-4 py-3 text-sm" onChange={(event) => setLinkEditor((current) => ({ ...current, sku: event.target.value }))} value={linkEditor.sku} />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Price</label>
                            <input className="field-control px-4 py-3 text-sm" min="0" onChange={(event) => setLinkEditor((current) => ({ ...current, price: event.target.value }))} step="0.01" type="number" value={linkEditor.price} />
                          </div>
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Default qty</label>
                            <input className="field-control px-4 py-3 text-sm" min="1" onChange={(event) => setLinkEditor((current) => ({ ...current, quantity: event.target.value }))} type="number" value={linkEditor.quantity} />
                          </div>
                        </div>
                        <div>
                          <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={savingLink} type="submit">
                            {savingLink ? "Saving..." : "Add mapping"}
                          </button>
                        </div>
                      </form>
                    </div>

                    <div className="space-y-3">
                      {selectedSupplier.links.length > 0 ? (
                        selectedSupplier.links.map((link) => (
                          <div className="field-soft p-4" key={link.id}>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div className="min-w-0">
                                {link.inventoryId && link.inventorySlug ? (
                                  <Link className="block truncate font-semibold text-nv-ink transition hover:text-nv-electric" to={`/shop/product/${link.inventorySlug}/${link.inventoryId}`}>
                                    {link.inventoryName}
                                  </Link>
                                ) : (
                                  <p className="font-semibold text-nv-ink">{link.inventoryName}</p>
                                )}
                                <p className="mt-1 text-sm text-nv-slate">{link.supplierProductName}</p>
                                <p className="mt-1 text-xs text-nv-slate">
                                  SKU {link.sku} · Default qty {link.quantity} · In stock {link.inventoryQuantity}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-nv-ink">{formatCurrency(link.price)}</p>
                                <button className="mt-3 rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300" onClick={() => void handleDeleteLink(link.id)} type="button">
                                  Remove
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">No product mappings yet for this supplier.</div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
